/**
 * FormField — vertical wrapper around a single form control.
 *
 *   ┌─────────────────────────────────┐
 *   │ Label (optional) [* required]   │
 *   ├─────────────────────────────────┤
 *   │ children — the control          │
 *   ├─────────────────────────────────┤
 *   │ Helper text / error message     │
 *   └─────────────────────────────────┘
 *
 * The wrapper is form-control-agnostic — it composes equally over
 * `<Input>`, `<Toggle>`, `<Checkbox>`, `<Radio>`, `<RadioGroup>`,
 * `<SegmentedButton>`, custom `<select>` markups, anything.
 *
 * State / error model:
 *   - `error={false}` (default) — helper text rendered in muted tone.
 *   - `error={true}` — helper text rendered in `palette.bad.main`.
 *   - `error="...message"` — string error replaces helperText AND
 *     triggers the error tone. If both helperText and string error are
 *     supplied, error wins.
 *
 * Accessibility:
 *   - The wrapper renders a `<label>` for the label slot. Pass
 *     `htmlFor={controlId}` to associate it with the underlying input.
 *     For groups (RadioGroup etc.) the label uses `id` + the group
 *     references it via `aria-labelledby`.
 *   - Helper and error text get auto-generated ids so they can be
 *     wired to the control via `aria-describedby` /
 *     `aria-errormessage`. Consumers pass `describedById` to the
 *     control to opt in.
 *
 * @format
 */

import {useId, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

const FieldRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  width: '100%',
}));

const LabelRow = styled('label')(({theme}) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  // 14 — нижняя граница текста продукта: подпись поля называет, что в
  // него вводят, и мельче основного текста быть не может.
  ...text('label.m'),
  color: surfaceVar('onSurface'),
}));

const RequiredMark = styled(Box)(({theme}) => ({
  color: theme.palette.bad.main,
}));

const Description = styled(Box)(({}) => ({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
}));

const HelperText = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$error: boolean}>(({theme, $error}) => ({
  ...text('body.m'),
  color: $error ? theme.palette.bad.main : surfaceVar('onSurfaceLight'),
}));

// ── Component ──────────────────────────────────────────────────────────

export interface FormFieldProps {
  /** Visible field label, rendered above the control. */
  label?: ReactNode;

  /**
   * `htmlFor` for the label, when the control inside has a stable id.
   * For groups (RadioGroup, SegmentedButton) prefer omitting this and
   * wiring `aria-labelledby` via the `labelId` returned id (not yet
   * surfaced — TODO).
   */
  htmlFor?: string;

  /**
   * Renders an asterisk after the label. Visual only — the underlying
   * control's `required` attribute is what the browser enforces.
   */
  required?: boolean;

  /**
   * Optional descriptive paragraph between label and control. Use for
   * "what does this control do" context that doesn't fit in the label.
   */
  description?: ReactNode;

  /**
   * Helper text rendered below the control. Plain when `error` is
   * false; replaced by the error message (or styled with error tone)
   * when error is set.
   */
  helperText?: ReactNode;

  /**
   * Error indicator. `false` is the default. `true` flips the helper
   * to the error tone. A string here replaces the helper with the
   * error message.
   */
  error?: boolean | string;

  /** The form control. Required. */
  children: ReactNode;

  className?: string;
}

export function FormField({
  label,
  htmlFor,
  required,
  description,
  helperText,
  error = false,
  children,
  className,
}: FormFieldProps) {
  const fallbackHelperId = useId();
  const fallbackErrorId = useId();
  const hasError = Boolean(error);
  const message = typeof error === 'string' ? error : helperText;

  return (
    <FieldRoot className={className}>
      {label != null && (
        <LabelRow htmlFor={htmlFor}>
          <Box component='span'>{label}</Box>
          {required && (
            <RequiredMark aria-hidden='true' title='required'>
              *
            </RequiredMark>
          )}
        </LabelRow>
      )}

      {description != null && <Description>{description}</Description>}

      {children}

      {message != null && (
        <HelperText
          $error={hasError}
          id={hasError ? fallbackErrorId : fallbackHelperId}
          role={hasError ? 'alert' : undefined}
        >
          {message}
        </HelperText>
      )}
    </FieldRoot>
  );
}
